/**
 * TransactionRef — the key that ties a comment to ONE booking.
 *
 * [BabProcess].[Comment].TransactionRef is compared as an exact string, not a foreign key,
 * and the same rows are commented from the BAB Process App. This builder is therefore a
 * verbatim port of `buildTransactionRef` in
 * `bab-process-app-v2/web/src/components/bab/BabAccountTransactionModal.tsx` —
 * property ORDER and the RAW (unsigned) amount are part of the key. Do not "improve" it:
 * any change silently orphans every existing booking comment in both apps.
 */
import type { BabTransaction } from '../types/babReport';
import { formatEurExact } from './babFormat';

export interface TransactionRefData {
    date: string;
    company: string;
    amount: number;
    desc: string;
    source: string;
    sourceId?: string;
}

export function buildTransactionRef(tx: BabTransaction): string {
    return JSON.stringify({
        date: tx.postingDate || `${tx.periodYear}-${String(tx.periodMonth).padStart(2, '0')}`,
        company: tx.companyCode,
        amount: tx.amountEUR,
        desc: tx.description || '',
        source: tx.dataSource,
        sourceId: tx.sourceId || undefined,
    });
}

export function parseTransactionRef(ref: string | null): TransactionRefData | null {
    if (!ref) return null;
    try {
        const parsed = JSON.parse(ref) as TransactionRefData;
        return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
        return null;
    }
}

/** "20.02.2026 · DBI · 10'355.66 EUR · Eidg. Steuerverwaltung… · Doc 1227127" */
export function describeTransactionRef(ref: string | null): string | null {
    const data = parseTransactionRef(ref);
    if (!data) return null;
    const date = /^\d{4}-\d{2}-\d{2}$/.test(data.date)
        ? data.date.split('-').reverse().join('.')
        : data.date;
    const parts = [date, (data.company || '').toUpperCase(), `${formatEurExact(data.amount)} EUR`];
    if (data.desc) parts.push(data.desc);
    if (data.sourceId) parts.push(`Doc ${data.sourceId}`);
    return parts.join(' · ');
}
